import Session from 'supertokens-auth-react/recipe/session';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * API client using fetch (not Axios) so SuperTokens session
 * interceptors work automatically for header-based auth.
 */
async function request<T>(
  method: string,
  url: string,
  data?: unknown,
  config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string> },
): Promise<{ data: T; status: number }> {
  const fullUrl = `${API_BASE_URL}/api${url}`;
  const headers: Record<string, string> = {
    ...config?.headers,
  };

  if (data && !(data instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(fullUrl, {
    method,
    headers,
    body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  if (response.status === 401) {
    // Try to refresh the session
    const refreshed = await Session.attemptRefreshingSession();
    if (refreshed) {
      // Retry the request
      const retryResponse = await fetch(fullUrl, {
        method,
        headers,
        body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      });
      return parseResponse<T>(retryResponse, config?.responseType);
    }
    // Redirect to login if refresh failed
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error: any = new Error(`API Error: ${response.status}`);
    error.response = {
      status: response.status,
      data: await response.json().catch(() => null),
    };
    throw error;
  }

  return parseResponse<T>(response, config?.responseType);
}

async function parseResponse<T>(
  response: Response,
  responseType?: 'blob' | 'json',
): Promise<{ data: T; status: number }> {
  if (responseType === 'blob') {
    const blob = await response.blob();
    return { data: blob as unknown as T, status: response.status };
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  return { data, status: response.status };
}

// API helper matching the same interface used throughout the app
export const api = {
  get: <T>(url: string, config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string> }) =>
    request<T>('GET', url, undefined, config),

  post: <T>(url: string, data?: unknown, config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string> }) =>
    request<T>('POST', url, data, config),

  put: <T>(url: string, data?: unknown, config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string> }) =>
    request<T>('PUT', url, data, config),

  patch: <T>(url: string, data?: unknown, config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string> }) =>
    request<T>('PATCH', url, data, config),

  delete: <T>(url: string, config?: { responseType?: 'blob' | 'json'; headers?: Record<string, string>; data?: unknown }) =>
    request<T>('DELETE', url, config?.data, config),
};

export default api;
